import React, { useState, useEffect } from 'react';
import { fetchCategories, fetchSubcategories, fetchCompanies } from '../services/api';
import { toast } from 'react-toastify';
import DateTimeInput from './DateTimeInput';

const ContestFormModal = ({ contest, onClose, onSave }) => {
  const isEditMode = !!contest;
  
  const [formData, setFormData] = useState({
    heading: '',
    requirement: '',
    category: '',
    subcategory: '',
    submissionStartDate: '',
    submissionEndDate: '',
    resultDate: '',
    isActive: true,
    companyId: '',
    prizes: [{ position: 1, amount: '' }]
  });
  
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  // Initialize form data when editing
  useEffect(() => {
    if (isEditMode && contest) {
      const startDate = contest.submissionStartDate 
        ? formatForInput(contest.submissionStartDate)
        : '';
      
      const endDate = contest.submissionEndDate
        ? formatForInput(contest.submissionEndDate)
        : '';

      const resDate = contest.resultDate
        ? formatForInput(contest.resultDate)
        : '';
      
      setFormData({
        heading: contest.heading || '',
        requirement: contest.requirement || '',
        category: contest.category || '',
        subcategory: contest.subcategory || '',
        submissionStartDate: startDate,
        submissionEndDate: endDate,
        resultDate: resDate,
        isActive: contest.isActive,
        companyId: contest.companyId || contest.createdBy?.id || '',
        prizes: contest.prizes?.length > 0 
          ? contest.prizes.map(prize => ({ 
              position: prize.position, 
              amount: prize.amount,
              id: prize.id
            }))
          : [{ position: 1, amount: '' }]
      });
    }
  }, [contest, isEditMode]);

  // Load categories, subcategories, and companies on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [categoriesData, companiesData] = await Promise.all([
          fetchCategories(),
          fetchCompanies({ size: 100 }) // Fetch with a larger size to get more companies
        ]);
        
        setCategories(categoriesData);
        setCompanies(companiesData.content || []);
        
        // If we're in edit mode, find the category ID by name
        if (isEditMode && contest?.category) {
          const categoryObj = categoriesData.find(cat => cat.name === contest.category);
          if (categoryObj) {
            setSelectedCategoryId(categoryObj.id.toString());
            // Load subcategories for the found category
            loadSubcategoriesForCategory(categoryObj.id);
          }
        }
      } catch (error) {
        toast.error('Failed to load form data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isEditMode, contest]);

  // Function to load subcategories for a specific category ID
  const loadSubcategoriesForCategory = async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchSubcategories(categoryId);
      setSubcategories(data || []);
    } catch (error) {
      toast.error('Failed to fetch subcategories');
      setSubcategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'category') {
      // Handle category change
      const categoryId = value;
      setSelectedCategoryId(categoryId);
      
      // Find the category name from the ID
      const categoryObj = categories.find(cat => cat.id.toString() === categoryId);
      const categoryName = categoryObj ? categoryObj.name : '';
      
      setFormData({
        ...formData,
        category: categoryName, // Store category name
        subcategory: '' // Reset subcategory when category changes
      });
      
      // Load subcategories for the selected category
      loadSubcategoriesForCategory(categoryId);
    } else if (name === 'subcategory') {
      // Handle subcategory change
      const subcategoryId = value;
      
      // Find the subcategory name from the ID
      const subcategoryObj = subcategories.find(subcat => subcat.id.toString() === subcategoryId);
      const subcategoryName = subcategoryObj ? subcategoryObj.name : '';
      
      setFormData({
        ...formData,
        subcategory: subcategoryName // Store subcategory name
      });
    } else if (['submissionStartDate','submissionEndDate','resultDate'].includes(name)) {
      const updatedData = {
        ...formData,
        [name]: value
      };
      setFormData(updatedData);
      // re-validate date fields live
      const dateFieldErrors = validateDateFields(updatedData);
      setErrors({
        ...errors,
        ...dateFieldErrors
      });
      return;
    } else {
      // Handle other fields normally
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    
    // Clear any error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handlePrizeChange = (index, field, value) => {
    const updatedPrizes = [...formData.prizes];
    updatedPrizes[index] = {
      ...updatedPrizes[index],
      [field]: field === 'amount' ? parseFloat(value) || '' : value
    };
    setFormData({
      ...formData,
      prizes: updatedPrizes
    });
  };

  const addPrize = () => {
    const nextPosition = formData.prizes.length + 1;
    setFormData({
      ...formData,
      prizes: [
        ...formData.prizes,
        { position: nextPosition, amount: '' }
      ]
    });
  };

  const removePrize = (index) => {
    if (formData.prizes.length === 1) {
      return; // Keep at least one prize
    }
    
    const updatedPrizes = formData.prizes.filter((_, i) => i !== index);
    // Update positions after removal
    const reorderedPrizes = updatedPrizes.map((prize, i) => ({
      ...prize,
      position: i + 1
    }));
    
    setFormData({
      ...formData,
      prizes: reorderedPrizes
    });
  };

  const validateDateFields = (data) => {
    const dateErrors = {};
    const { submissionStartDate, submissionEndDate, resultDate } = data;

    if (!submissionStartDate) {
      dateErrors.submissionStartDate = 'Start date/time is required';
    } else if (!isEditMode && new Date(submissionStartDate) < new Date()) {
      dateErrors.submissionStartDate = 'Start date/time cannot be in the past';
    }

    if (!submissionEndDate) {
      dateErrors.submissionEndDate = 'End date/time is required';
    } else if (submissionStartDate && new Date(submissionStartDate) >= new Date(submissionEndDate)) {
      dateErrors.submissionEndDate = 'End date/time must be after start date/time';
    }

    if (!resultDate) {
      dateErrors.resultDate = 'Result date/time is required';
    } else if (submissionEndDate && new Date(submissionEndDate) >= new Date(resultDate)) {
      dateErrors.resultDate = 'Result date/time must be after end date/time';
    }
    return dateErrors;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.heading.trim()) {
      newErrors.heading = 'Heading is required';
    }
    
    if (!formData.requirement.trim()) {
      newErrors.requirement = 'Requirement is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    if (!formData.subcategory) {
      newErrors.subcategory = 'Subcategory is required';
    }
    
    Object.assign(newErrors, validateDateFields(formData));
    
    if (!formData.companyId) {
      newErrors.companyId = 'Sponsor is required';
    }
    
    const prizeErrors = [];
    formData.prizes.forEach((prize, index) => {
      const prizeError = {};
      if (!prize.amount || prize.amount <= 0) {
        prizeError.amount = 'Valid prize amount is required';
        prizeErrors[index] = prizeError;
      }
    });
    
    if (prizeErrors.length > 0) {
      newErrors.prizes = prizeErrors;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const appendSeconds = (val) => (val ? `${val}:00` : '');
      const formattedData = {
        ...formData,
        submissionStartDate: appendSeconds(formData.submissionStartDate),
        submissionEndDate: appendSeconds(formData.submissionEndDate),
        resultDate: appendSeconds(formData.resultDate)
      };
      onSave(formattedData);
    }
  };

  // helper to convert ISO date to local datetime-local value
  const formatForInput = (isoDate) => {
    const d = new Date(isoDate);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  {isEditMode ? 'Update Contest' : 'Create Contest'}
                </h3>
                <div className="mt-4">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Heading */}
                    <div>
                      <label htmlFor="heading" className="block text-sm font-medium text-gray-700">
                      Contest Name*
                      </label>
                      <input
                        type="text"
                        name="heading"
                        id="heading"
                        value={formData.heading}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                          errors.heading ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      />
                      {errors.heading && (
                        <p className="mt-1 text-sm text-red-600">{errors.heading}</p>
                      )}
                    </div>

                    {/* Requirement */}
                    <div>
                      <label htmlFor="requirement" className="block text-sm font-medium text-gray-700">
                        Requirement*
                      </label>
                      <textarea
                        name="requirement"
                        id="requirement"
                        rows="3"
                        value={formData.requirement}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                          errors.requirement ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      ></textarea>
                      {errors.requirement && (
                        <p className="mt-1 text-sm text-red-600">{errors.requirement}</p>
                      )}
                    </div>

                    {/* Company */}
                    <div>
                      <label htmlFor="companyId" className="block text-sm font-medium text-gray-700">
                        Sponsor*
                      </label>
                      <select
                        name="companyId"
                        id="companyId"
                        value={formData.companyId}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                          errors.companyId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                        disabled={isLoading}
                      >
                        <option value="">Select Company</option>
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                      {errors.companyId && (
                        <p className="mt-1 text-sm text-red-600">{errors.companyId}</p>
                      )}
                    </div>

                    {/* Category and Subcategory */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                          Category*
                        </label>
                        <select
                          name="category"
                          id="category"
                          value={selectedCategoryId}
                          onChange={handleChange}
                          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                            errors.category ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                          }`}
                          disabled={isLoading}
                        >
                          <option value="">Select Category</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                        {errors.category && (
                          <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="subcategory" className="block text-sm font-medium text-gray-700">
                          Subcategory*
                        </label>
                        <select
                          name="subcategory"
                          id="subcategory"
                          value={subcategories.find(s => s.name === formData.subcategory)?.id || ''}
                          onChange={handleChange}
                          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                            errors.subcategory ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                          }`}
                          disabled={!selectedCategoryId || isLoading}
                        >
                          <option value="">Select Subcategory</option>
                          {subcategories.map((subcat) => (
                            <option key={subcat.id} value={subcat.id}>
                              {subcat.name}
                            </option>
                          ))}
                        </select>
                        {errors.subcategory && (
                          <p className="mt-1 text-sm text-red-600">{errors.subcategory}</p>
                        )}
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="submissionStartDate" className="block text-sm font-medium text-gray-700">
                          Submission Start Date/Time*
                        </label>
                        <DateTimeInput
                          name="submissionStartDate"
                          value={formData.submissionStartDate}
                          onChange={handleChange}
                          error={errors.submissionStartDate}
                        />
                      </div>

                      <div>
                        <label htmlFor="submissionEndDate" className="block text-sm font-medium text-gray-700">
                          Submission End Date/Time*
                        </label>
                        <DateTimeInput
                          name="submissionEndDate"
                          value={formData.submissionEndDate}
                          onChange={handleChange}
                          error={errors.submissionEndDate}
                        />
                      </div>
                    </div>

                    {/* Result Date */}
                    <div>
                      <label htmlFor="resultDate" className="block text-sm font-medium text-gray-700">
                        Result Date/Time*
                      </label>
                      <DateTimeInput
                        name="resultDate"
                        value={formData.resultDate}
                        onChange={handleChange}
                        error={errors.resultDate}
                      />
                    </div>

                    {/* Status */}
                    <div className="flex items-center">
                      <input
                        id="isActive"
                        name="isActive"
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                        Active Contest
                      </label>
                    </div>

                    {/* Prizes */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">Prizes*</label>
                        <button
                          type="button"
                          onClick={addPrize}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Add Prize
                        </button>
                      </div>
                      
                      {formData.prizes.map((prize, index) => (
                        <div key={index} className="flex items-center space-x-2 mb-2">
                          <div className="w-16">
                            <label className="sr-only">Position</label>
                            <input
                              type="number"
                              value={prize.position}
                              disabled
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            />
                          </div>
                          <div className="flex-grow">
                            <label className="sr-only">Prize Amount</label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 sm:text-sm">$</span>
                              </div>
                              <input
                                type="number"
                                value={prize.amount}
                                onChange={(e) => handlePrizeChange(index, 'amount', e.target.value)}
                                className={`block w-full pl-7 pr-12 rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                                  errors.prizes && errors.prizes[index]?.amount ? 'border-red-300' : ''
                                }`}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                              />
                            </div>
                            {errors.prizes && errors.prizes[index]?.amount && (
                              <p className="mt-1 text-sm text-red-600">{errors.prizes[index].amount}</p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removePrize(index)}
                            disabled={formData.prizes.length <= 1}
                            className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSubmit}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {isEditMode ? 'Update' : 'Create'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContestFormModal; 